import { CheckCircle, ArrowRight, Users, Calendar, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const UniteNationTech = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-yellow-50/30">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Unite Nation Tech
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Marketing membership for Managed Service Providers to access tools and resources that help you market your business, connect and learn with other MSPs.
          </p>
        </div>
      </section>

      {/* Description */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-lg text-gray-700 leading-relaxed">
            We have been working with MSPs across New Zealand and have achieved great success for them.
          </p>
        </div>
      </section>

      {/* Hero Image */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=675&fit=crop"
              alt="MSP Team Collaboration"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* What is it */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                What is it?
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed">
                It is a monthly membership where you receive planning tools and guidelines to set your marketing up and learn ways to be efficient.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop"
                alt="Planning and strategy"
                className="rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You Receive */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            What do I receive?
          </h2>
          <div className="space-y-6">
            {[
              '2 Blogs written specifically for the MSP environment',
              '8 Social media posts including images and written content',
              '1 Unite Tech Online Networking Mixer & Training per month'
            ].map((item, index) => (
              <Card key={index} className="border-2 hover:border-[#FBD116] transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-full bg-[#FBD116] text-gray-900 font-bold flex items-center justify-center mr-4 flex-shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-lg text-gray-900">{item}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why Unite Nation Tech?
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Users,
                title: 'Connect & Learn',
                description: 'Network with other MSPs and learn from their experiences and successes.'
              },
              {
                icon: TrendingUp,
                title: 'Ready-to-Use Content',
                description: 'Get professionally crafted blogs and social media posts tailored for MSPs.'
              },
              {
                icon: Calendar,
                title: 'Monthly Training',
                description: 'Join exclusive online networking mixers and training sessions.'
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-2 hover:border-[#FBD116] transition-all duration-300 hover:-translate-y-1">
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 rounded-full bg-[#FBD116]/10 flex items-center justify-center mx-auto mb-6">
                      <Icon className="w-8 h-8 text-gray-900" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How much?
            </h2>
            <p className="text-2xl text-gray-700">
              The first month is <span className="font-bold text-[#FBD116]">free</span>, and <span className="font-bold">$125/month</span> thereafter
            </p>
          </div>
          <div className="text-center">
            <Button
              asChild
              size="lg"
              className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
            >
              <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
                Subscribe Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* Bottom Image */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="aspect-video rounded-2xl overflow-hidden shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1557804506-669a67965ba0?w=1200&h=675&fit=crop"
              alt="MSP Success"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">
            Want to know more?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Have a chat with our Unite Nation Co-Ordinator.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-[#FBD116] hover:bg-[#E5C015] text-gray-900 font-semibold text-lg px-8"
          >
            <a href="https://calendly.com/bronny-mpremarketing/30min" target="_blank" rel="noopener noreferrer">
              Let's Chat
              <ArrowRight className="ml-2 w-5 h-5" />
            </a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default UniteNationTech;
